import { resolveFlag } from '../utils/featureFlags';

// PostHog is loaded by the inline snippet in public/index.html (kept identical
// in app/templates/index.html), not bundled, so it starts -- and sends the
// landing $pageview -- without waiting for this bundle. The snippet also owns
// the config: proxy host, masking, scroll depth, and `capture_pageview:
// 'history_change'`, which captures a $pageview whenever the pathname changes
// and only then (a query- or hash-only change is not a new page).
//
// `window.posthog` is the snippet's stub until the library arrives -- calls on
// it are queued and replayed in order -- and the real instance after. So read it
// at call time, never cache it, and never gate a call on `__loaded`: that is
// false on the stub and would drop exactly the early events the stub exists to
// keep. Absent entirely in react-snap, on the dev server, and in tests.
export const getPostHog = () => (typeof window !== 'undefined' && window.posthog) || null;

// Capture options for an event sent right before the page navigates away (an
// Auth0 or Stripe redirect): skip the batch queue and send by beacon, or the
// navigation can drop it (#312).
export const SEND_BEFORE_NAVIGATION = { send_instantly: true, transport: 'sendBeacon' };

// Helper to identify users (call this when user logs in)
export const identifyUser = (userId, userProperties = {}) => {
  try {
    const posthog = getPostHog();
    if (posthog) {
      posthog.identify(userId, {
        ...userProperties,
        $set: {
          ...userProperties,
          last_login: new Date().toISOString()
        }
      });
    }
  } catch (error) {
    console.error('PostHog identify error:', error);
  }
};

// Helper to set user properties
export const setUserProperties = (properties) => {
  try {
    const posthog = getPostHog();
    // setPersonProperties, not people.set: the stub queues the former only.
    if (posthog) posthog.setPersonProperties(properties);
  } catch (error) {
    console.error('PostHog set properties error:', error);
  }
};

// ---- Feature flags (A/B testing) ----
// The `decide` API is enabled in the snippet config, so flags resolve client-side.
// Read flags reactively in components via the useFeatureFlag hook. Until the
// library loads, resolveFlag sees the stub (no `__loaded`) and returns the fallback.
export const getFeatureFlag = (key, fallback = false) => resolveFlag(getPostHog(), key, fallback);

export const isFeatureEnabled = (key) => resolveFlag(getPostHog(), key, false) === true;

// Subscribe to flag readiness/changes; returns an unsubscribe fn (noop if unavailable).
// Subscribing on the stub is deliberate: the call is queued and registered when
// the library loads, which is exactly when flags first become readable. But the
// stub returns no unsubscribe, so a component that unmounts before the library
// loads could never remove its listener; `active` makes the late-registered
// callback inert instead.
export const onFeatureFlags = (callback) => {
  try {
    const posthog = getPostHog();
    if (posthog && typeof posthog.onFeatureFlags === 'function') {
      let active = true;
      const unsub = posthog.onFeatureFlags((...args) => { if (active) callback(...args); });
      return () => {
        active = false;
        if (typeof unsub === 'function') unsub();
      };
    }
  } catch (error) {
    console.error('PostHog onFeatureFlags error:', error);
  }
  return () => {};
};

// Session replay runs on these routes only (see useSessionRecording).
// The quiz finishes with history.replaceState to its /result/ URL, which the
// router never sees, so one recording covers the questions, email and result.
export const RECORDED_ROUTES = ['/start', '/quiz/bettor-type'];

export const isRecordedRoute = (pathname) =>
  RECORDED_ROUTES.includes((pathname || '/').replace(/\/+$/, '') || '/');

// The recorder ships in our own bundle, as a chunk loaded only when a recorded
// route mounts: disable_external_dependency_loading stops PostHog fetching it
// from its CDN, and without it startSessionRecording records nothing. It
// registers itself on window.__PosthogExtensions__, where the snippet-loaded
// library looks for it; both come from the same installed posthog-js version.
// It must be lazy-recorder, not recorder: the library (checked on 1.438.12)
// needs initSessionRecording as well as rrweb, and dist/recorder registers only
// rrweb, so with it replay stays in 'lazy_loading' and sends nothing, silently.
const loadRecorder = () => import('posthog-js/dist/lazy-recorder');

/** Start replay for a recorded route. Resolves false when PostHog is not running. */
export const startRouteRecording = async () => {
  if (!getPostHog()) return false;
  try {
    await loadRecorder();
    // Re-read: the library may have replaced the stub while the chunk loaded.
    getPostHog().startSessionRecording();
    return true;
  } catch (error) {
    console.error('PostHog session recording error:', error);
    return false;
  }
};

export const stopRouteRecording = () => {
  try {
    const posthog = getPostHog();
    if (posthog) posthog.stopSessionRecording();
  } catch (error) {
    console.error('PostHog session recording error:', error);
  }
};
